/**
 * Densification des séries de snapshots.
 *
 * ── Le problème ─────────────────────────────────────────────────────────────
 * `accountsTimeline` ne contient QUE les jours effectivement mesurés : l'API
 * lit `daily_snapshots` sans combler les manques, et c'est volontaire (un jour
 * absent = sync en échec, donnée perdue à jamais, la prod ne conserve aucun
 * historique d'abonnement).
 *
 * Mais un tableau sans les jours manquants ne suffit pas à les rendre visibles :
 * Recharts relie simplement les points fournis, et trace une belle droite
 * par-dessus le trou. La courbe raconte alors une progression régulière là où
 * personne n'a mesuré — le mensonge que l'omission était censée éviter.
 *
 * ── La correction ───────────────────────────────────────────────────────────
 * On reconstruit la suite complète des jours de la période et on insère un
 * point à `null` pour chaque jour absent. Avec `connectNulls={false}`, le trait
 * s'interrompt : le lecteur voit qu'il n'y a pas eu de mesure.
 *
 * `null` et non `0` : zéro serait un effondrement du nombre de comptes qui n'a
 * jamais eu lieu.
 */

import { shiftDay } from './dates';
import type { AccountsPoint } from '../types';

/** Un point de la courbe, où chaque mesure peut manquer. */
export interface NullableAccountsPoint {
  date: string;
  totalAccounts: number | null;
  totalSubscribers: number | null;
  annualSubscribers: number | null;
  monthlySubscribers: number | null;
}

/**
 * Complète la série avec un point nul par jour manquant.
 *
 * Bornée par sécurité : une période de deux ans fait 730 points, mais une
 * période aberrante ne doit pas pouvoir faire boucler indéfiniment.
 */
export function withMissingDays(
  points: readonly AccountsPoint[],
  from: string,
  to: string,
  maxDays = 800,
): NullableAccountsPoint[] {
  if (points.length === 0) return [];

  const byDate = new Map(points.map((point) => [point.date, point]));
  const filled: NullableAccountsPoint[] = [];

  let cursor = from;
  for (let guard = 0; cursor <= to && guard < maxDays; guard += 1) {
    const point = byDate.get(cursor);
    filled.push(
      point
        ? {
            date: point.date,
            totalAccounts: point.totalAccounts,
            totalSubscribers: point.totalSubscribers,
            annualSubscribers: point.annualSubscribers,
            monthlySubscribers: point.monthlySubscribers,
          }
        : {
            date: cursor,
            totalAccounts: null,
            totalSubscribers: null,
            annualSubscribers: null,
            monthlySubscribers: null,
          },
    );
    cursor = shiftDay(cursor, 1);
  }

  return filled;
}

/** Nombre de jours sans snapshot, pour l'annoncer plutôt que de le laisser deviner. */
export function countMissingDays(points: readonly NullableAccountsPoint[]): number {
  return points.filter((point) => point.totalAccounts === null).length;
}
